// Route Protection Configuration
// Date: January 13, 2025
// Purpose: Maps all routes to their corresponding navigation screen codes for RBAC protection

/**
 * Section-based route handling
 *
 * For paths like /templates/hierarchy/:templateId/edit we do NOT store resource IDs.
 * We map the first path segment after /templates/ to one screen; that screen covers
 * all sub-routes (list, create, edit by ID). Add new template types here and register
 * one screen in the DB; no per-resource routes needed.
 */
const TEMPLATES_SECTION_TO_SCREEN: Record<string, string> = {
  'manage': 'TEMPLATE_MANAGEMENT',
  'hierarchy': 'HIERARCHY_TEMPLATES',
  'roadmap': 'ROADMAP_TEMPLATES',
  'role': 'ROLE_TEMPLATES',
  'project-structure': 'PROJECT_STRUCTURE_TEMPLATES',
  'comprehensive-builder': 'ROADMAP_TEMPLATE_COMPREHENSIVE_BUILDER',
  'project-structure-builder': 'PROJECT_STRUCTURE_TEMPLATES',
};

/**
 * Route to Screen Code Mapping
 * 
 * This map defines which navigation screen controls access to each route.
 * Routes are matched by path prefix (most specific first).
 * For /templates/<section>/... use TEMPLATES_SECTION_TO_SCREEN instead of adding
 * every possible path here.
 * 
 * Format: route path -> screen code
 */
export const routeToScreenMap: Record<string, string> = {
  // Business Management Routes
  '/dashboard': 'DASHBOARD',
  '/transformations': 'TRANSFORMATIONS',
  '/kpis': 'KPI_MANAGEMENT',
  '/kpis/loads': 'BUSINESS_PERFORMANCE_BULK_ACTUALS',
  '/process-models': 'PROCESS_MODELS',
  '/processes': 'PROCESS_MODELS',
  '/change-management': 'CHANGE_MANAGEMENT',
  '/risk': 'RISK_MANAGEMENT',
  '/issues': 'ISSUE_MANAGEMENT',
  '/resources': 'RESOURCES',
  '/reports': 'REPORTS',
  '/settings': 'SETTINGS',
  '/settings/profile': 'PROFILE',
  '/settings/subscription': 'MY_SUBSCRIPTION',
  '/settings/tenant-subscription-configuration': 'TENANT_CONFIGURATION',
  '/tenant-subscription-configuration': 'TENANT_CONFIGURATION',
  // Legacy path kept for backwards compatibility
  '/settings/tenant-configuration': 'TENANT_CONFIGURATION',
  '/tenant-configuration': 'TENANT_CONFIGURATION',
  '/actions': 'ACTION_MANAGEMENT',
  '/partner/received-invitations': 'PARTNER_INVITATIONS',
  '/pe-portfolio': 'PE_PORTFOLIO',
  '/benchmarking': 'BENCHMARKING',
  '/admin/process-admin': 'BENCHMARKING_PROCESS_ADMIN',
  '/data-import': 'DATA_IMPORT',
  '/education': 'EDUCATION',
  '/marketplace': 'ROADMAP_MARKETPLACE',
  '/tenant/planning': 'AI_PLANNING',
  '/tenant/roadmaps': 'ROADMAP_MARKETPLACE_TENANT',
  // Data Connection KPI stubs removed; sources only until live connectors are in scope
  '/data-connection/sources': 'DATA_SOURCE_MANAGER',
  '/data-connection': 'DATA_SOURCE_MANAGER',
  '/crm': 'CRM',
  '/stakeholders': 'STAKEHOLDERS',
  '/calendar': 'CALENDAR',
  '/discussions': 'DISCUSSIONS',
  '/alignment': 'ALIGNMENT',
  '/hierarchies': 'HIERARCHIES',
  '/business-hierarchy': 'BUSINESS_HIERARCHY',
  '/functional-hierarchy': 'FUNCTIONAL_HIERARCHY',
  '/geographic-hierarchy': 'GEOGRAPHIC_HIERARCHY',
  '/ai': 'AI_FEATURES',
  '/ai-studio': 'AI_STUDIO_HOME',
  '/ai-studio/catalog': 'AI_STUDIO_CATALOG',
  '/ai-studio/conversations': 'AI_STUDIO_CONVERSATION',
  // Catalog, agent detail, and version editor share one screen: one capability,
  // one permission boundary (Design §19.3).
  '/ai-studio/agents': 'AI_STUDIO_AGENTS',
  // Pack catalog + editor share one screen (Design §19.3 S4).
  '/ai-studio/packs': 'AI_STUDIO_PACKS',
  '/ai-studio/instances': 'AI_STUDIO_INSTANCES',
  // Legacy bindings URLs redirect to instances (same deploy rows).
  '/ai-studio/bindings': 'AI_STUDIO_INSTANCES',
  // S6 run catalog + S7 workspace share one permission boundary.
  '/ai-studio/runs': 'AI_STUDIO_RUNS',
  '/ai-studio/history': 'AI_STUDIO_HISTORY',
  // S9 ontology browser (WF-F6) — Platform Admin foundation.
  '/ai-studio/ontology': 'AI_STUDIO_ONTOLOGY',
  
  // Performance Dashboard (legacy /business-performance URLs still map)
  '/performance': 'BUSINESS_PERFORMANCE_DASHBOARD',
  '/performance/plans/:id': 'BUSINESS_PERFORMANCE_PLAN_HEALTH',
  '/performance/objectives/:id': 'BUSINESS_PERFORMANCE_OBJECTIVE_PERFORMANCE',
  '/performance/target-vs-actual': 'BUSINESS_PERFORMANCE_TARGET_VS_ACTUAL',
  '/performance/trends/:objectiveId': 'BUSINESS_PERFORMANCE_HISTORICAL_TRENDS',
  '/performance/enter-actuals': 'BUSINESS_PERFORMANCE_ENTER_ACTUALS',
  '/performance/bulk-actuals': 'BUSINESS_PERFORMANCE_BULK_ACTUALS',
  '/performance/kpis/:id': 'BUSINESS_PERFORMANCE_KPI_HISTORY',
  '/business-performance': 'BUSINESS_PERFORMANCE_DASHBOARD',
  '/business-performance/plans/:id': 'BUSINESS_PERFORMANCE_PLAN_HEALTH',
  '/business-performance/objectives/:id': 'BUSINESS_PERFORMANCE_OBJECTIVE_PERFORMANCE',
  '/business-performance/target-vs-actual': 'BUSINESS_PERFORMANCE_TARGET_VS_ACTUAL',
  '/business-performance/trends/:objectiveId': 'BUSINESS_PERFORMANCE_HISTORICAL_TRENDS',
  '/business-performance/enter-actuals': 'BUSINESS_PERFORMANCE_ENTER_ACTUALS',
  '/business-performance/bulk-actuals': 'BUSINESS_PERFORMANCE_BULK_ACTUALS',
  '/business-performance/kpis/:id': 'BUSINESS_PERFORMANCE_KPI_HISTORY',
  
  // Platform Admin Routes
  '/user-management': 'USER_MANAGEMENT',
  '/tenants': 'TENANT_MANAGEMENT',
  '/admin/dashboard': 'ADMIN_DASHBOARD',
  '/admin/notifications': 'ADMIN_NOTIFICATIONS',
  '/admin/logs': 'SYSTEM_LOGS',
  '/admin/log-files': 'LOG_FILES',
  '/admin/activity': 'ACTIVITY_DASHBOARD',
  '/admin/billing': 'ADMIN_BILLING',
  '/admin/subscription': 'TENANT_SUBSCRIPTION',
  '/admin/ai/settings': 'ADMIN_AI_SETTINGS',
  '/tenant/ai/settings': 'TENANT_AI_SETTINGS',
  '/admin/issue-categories': 'ISSUE_CATEGORIES',
  '/admin/risk-categories': 'RISK_CATEGORIES',
  '/admin/content-management': 'CONTENT_MANAGEMENT',
  '/admin/maintenance': 'MAINTENANCE_MODE',
  '/admin/password-management': 'PASSWORD_MANAGEMENT',
  '/admin/user-approvals': 'USER_APPROVALS',
  '/admin/partner-management': 'PARTNER_MANAGEMENT',
  '/admin/provisioning-profiles': 'PROVISIONING_PROFILES',
  '/admin/user-positions': 'USER_POSITION_MANAGEMENT',
  '/admin/position-templates': 'POSITION_TEMPLATES',
  '/admin/positions': 'TENANT_POSITIONS',
  
  // System Maintenance Routes
  '/admin/system/settings': 'SYSTEM_SETTINGS',
  '/admin/system-monitoring': 'SYSTEM_MONITORING',
  '/admin/system': 'SYSTEM_MONITORING',
  '/admin/performance-tuning': 'PERFORMANCE_TUNING',
  '/admin/backup-recovery': 'BACKUP_RECOVERY',
  '/admin/audit-logging': 'AUDIT_LOGGING',
  
  // Access Management Routes
  '/admin/abac': 'ABAC_MANAGEMENT',
  '/admin/custom-attributes': 'CUSTOM_ATTRIBUTES',
  '/admin/value-assignment': 'VALUE_ASSIGNMENT',
  '/admin/policy-creation': 'POLICY_CREATION',
  '/admin/policy-assignment': 'POLICY_ASSIGNMENT',
  '/access-management': 'ACCESS_MANAGEMENT',
  
  // Navigation Management Routes
  '/admin/navigation/hierarchy': 'NAVIGATION_HIERARCHY',
  '/admin/navigation/modules': 'MODULE_MANAGEMENT',
  '/admin/navigation/screens': 'SCREEN_MANAGEMENT',
  '/admin/navigation/nodes': 'NODE_MANAGEMENT',
  '/admin/navigation/permissions': 'PERMISSION_MAPPING',
  '/admin/navigation-management': 'NAVIGATION_HIERARCHY',
  
  // Template Routes - detail routes (/templates/hierarchy/:id/edit) are handled by TEMPLATES_SECTION_TO_SCREEN
  '/templates/manage': 'TEMPLATE_MANAGEMENT',
  '/templates/hierarchy': 'HIERARCHY_TEMPLATES',
  '/templates/roadmap': 'ROADMAP_TEMPLATES',
  '/templates/role': 'ROLE_TEMPLATES',
  '/templates/project-structure': 'PROJECT_STRUCTURE_TEMPLATES',
  
  // Module Routes - These are handled by ModulePage which checks module-level permissions
  // We map them to a generic module screen code, but ModulePage will handle the actual permission check
  '/modules': 'MODULE_ACCESS',
  
  // RBAC Routes
  '/rbac/roles': 'ROLE_MANAGEMENT',
};

/**
 * Get the base route path to use for RBAC screen lookup.
 * Sub-routes like /stakeholders/create use the parent screen path so the backend
 * can find the navigation screen (e.g. /stakeholders) and grant the same permission.
 */
export function getBasePathForRBAC(path: string): string {
  if (path.startsWith('/modules/')) {
    return path;
  }
  if (routeToScreenMap[path]) {
    return path;
  }
  const sortedRoutes = Object.keys(routeToScreenMap).sort((a, b) => b.length - a.length);
  for (const route of sortedRoutes) {
    const routePattern = route.replace(/:\w+/g, '[^/]+');
    const routeRegex = new RegExp(`^${routePattern}(?:/|$)`);
    if (routeRegex.test(path)) {
      return route;
    }
  }
  for (const route of sortedRoutes) {
    if (!route.includes(':') && path.startsWith(route + '/')) {
      return route;
    }
  }
  for (const route of sortedRoutes) {
    if (!route.includes(':') && path.startsWith(route)) {
      return route;
    }
  }
  return path;
}

/**
 * Get screen code for a route path
 * 
 * @param path - Route path (e.g., '/dashboard', '/admin/maintenance')
 * @returns Screen code or null if not found
 */
/**
 * Get screen code for a route path
 * 
 * NOTE: This function is ONLY for direct screen routes (e.g., /dashboard, /kpis).
 * Module paths (e.g., /modules/business-management/templates) are handled dynamically
 * by ModulePage and should NOT be in this mapping. This allows modules to be moved
 * around in the navigation hierarchy without requiring code changes.
 * 
 * @param path - Route path (e.g., '/dashboard', '/admin/maintenance')
 * @returns Screen code or null if not found (or if it's a module path)
 */
export function getScreenCodeForRoute(path: string): string | null {
  // Special handling for module paths - these are handled by ModulePage
  // ModulePage will check permissions for the specific module, not a screen
  // This allows modules to be moved around in navigation without code changes
  if (path.startsWith('/modules/')) {
    // Return null to indicate this is a module path that should be handled differently
    // RBACRoute will allow access if screen code is null/undefined for module paths
    return null;
  }
  
  // Exact match first
  if (routeToScreenMap[path]) {
    return routeToScreenMap[path];
  }
  
  // Try pattern matching for parameterized routes (most specific first)
  const sortedRoutes = Object.keys(routeToScreenMap).sort((a, b) => b.length - a.length);
  for (const route of sortedRoutes) {
    // Convert route pattern to regex (e.g., '/business-performance/plans/:id' -> '/business-performance/plans/[^/]+')
    const routePattern = route.replace(/:\w+/g, '[^/]+');
    const routeRegex = new RegExp(`^${routePattern}(?:/|$)`);
    
    if (routeRegex.test(path)) {
      return routeToScreenMap[route];
    }
  }
  
  // Fallback to prefix matching for non-parameterized routes
  for (const route of sortedRoutes) {
    // Only use prefix matching if route doesn't contain parameters (no ':' in route)
    if (!route.includes(':') && path.startsWith(route)) {
      return routeToScreenMap[route];
    }
  }

  // Section-based: /templates/<section>/... — one screen per section, no resource IDs in DB
  if (path.startsWith('/templates/')) {
    const segments = path.slice(1).split('/').filter(Boolean);
    const section = segments[1];
    if (section && section in TEMPLATES_SECTION_TO_SCREEN) {
      return TEMPLATES_SECTION_TO_SCREEN[section];
    }
    // Unknown section under /templates/ → use generic template management
    if (segments.length >= 1) {
      return 'TEMPLATE_MANAGEMENT';
    }
  }

  return null;
}

/**
 * Backend-only routes that handle their own authorization
 * These routes should NOT be protected by frontend RBAC
 * The backend handles authorization for these routes
 */
export const backendOnlyRoutes: string[] = [
  // Add routes here that are backend-only and handle their own authorization
  // Example: '/admin/maintenance' if it has backend middleware that checks permissions
];

/**
 * Check if a route is backend-only (handles its own authorization)
 * 
 * @param path - Route path
 * @returns true if route is backend-only
 */
export function isBackendOnlyRoute(path: string): boolean {
  // Exact match
  if (backendOnlyRoutes.includes(path)) {
    return true;
  }
  
  // Prefix match (for nested routes)
  return backendOnlyRoutes.some(route => path.startsWith(route));
}

/**
 * Check if a route should be protected by frontend RBAC
 * 
 * @param path - Route path
 * @returns true if route should be protected by frontend RBAC
 */
export function shouldProtectRoute(path: string): boolean {
  // Backend-only routes handle their own authorization - skip frontend protection
  if (isBackendOnlyRoute(path)) {
    return false;
  }
  
  // Public routes that don't need protection
  const publicRoutes = [
    '/',
    '/login',
    '/register',
    '/register-enhanced',
    '/confirm-email',
    '/forgot-password',
    '/reset-password',
    '/why-tmos',
    '/product',
    '/solutions',
    '/privacy',
    '/terms',
    '/request-demo',
    '/maintenance',
  ];
  
  if (publicRoutes.includes(path)) {
    return false;
  }
  
  // All other routes should be protected by frontend RBAC
  return true;
}

